import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getProducts, createProduct, updateProduct, deleteProduct, getProductCategories, reorderProducts } from '../../api/products';
import { BASE_URL } from '../../api/inquiries';
import { fetchWithAuth } from '../../api/apiClient';
import ProductCategoryManager from '../../components/admin/ProductCategoryManager';
import AdminButton from '../../components/admin/AdminButton';
import AiImageGeneratorModal from '../../components/admin/common/AiImageGeneratorModal';
import AiPresetManager from '../../components/admin/AiPresetManager';

const ProductManagement = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]); // Dynamic categories
    const [loading, setLoading] = useState(true);

    // Modal & Form State
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
    const [reorderList, setReorderList] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isProcessingImage, setIsProcessingImage] = useState(false);
    const [isCropping, setIsCropping] = useState(false);
    
    // Inline AI Generator State
    const [isAiGenerating, setIsAiGenerating] = useState(false);
    const [aiFileUrls, setAiFileUrls] = useState([]);
    const [aiAttachedFiles, setAiAttachedFiles] = useState([]);
    const [isAiUploading, setIsAiUploading] = useState(false);
    const [aiCustomPrompt, setAiCustomPrompt] = useState('');

    const [isAiImageModalOpen, setIsAiImageModalOpen] = useState(false);

    const [aiScrapeUrl, setAiScrapeUrl] = useState('');
    const [isScraping, setIsScraping] = useState(false);

    const handleScrapeUrl = async () => {
        if (!aiScrapeUrl.trim()) return;
        setIsScraping(true);
        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/ai/scrape-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: aiScrapeUrl.trim() })
            });
            if (!res.ok) throw new Error('Scrape failed');
            const data = await res.json();
            
            if (data.success) {
                if (data.text) {
                    const textFile = new File([data.text], `scraped-page-${Date.now()}.txt`, { type: 'text/plain' });
                    // Using the existing AI file upload flow directly
                    await handleAiFileUploadInline({ target: { files: [textFile] } });
                }
                if (data.imageUrls && data.imageUrls.length > 0) {
                    // Inject multiple external URLs seamlessly
                    setFormData(prev => ({ ...prev, images: [...(prev.images || []), ...data.imageUrls] }));
                }
                setAiScrapeUrl('');
                alert('URL 분석이 완료되어 텍스트 첨부 파일 및 썸네일을 갤러리에 추가했습니다.');
            }
        } catch (err) {
            console.error(err);
            alert('URL 정보를 불러오는데 실패했습니다.');
        } finally {
            setIsScraping(false);
        }
    };

    const handleAiFileUploadInline = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setIsAiUploading(true);
        try {
            const newFileUrls = [];
            const newAttachedFiles = [];

            for (const file of files) {
                const uploadData = new FormData();
                uploadData.append('file', file);
                const res = await fetchWithAuth(`${BASE_URL}/api/upload?folder=tmp`, {
                    method: 'POST', body: uploadData
                });
                if (!res.ok) throw new Error(`Upload failed for ${file.name}`);
                const data = await res.json();
                newFileUrls.push(data.url || data.path);
                newAttachedFiles.push(file);
            }

            setAiFileUrls(prev => [...prev, ...newFileUrls]);
            setAiAttachedFiles(prev => [...prev, ...newAttachedFiles]);
        if (e.target && e.target.value !== undefined) {
            e.target.value = ''; 
        }
            
            // Auto Extract Media Asynchronously (Non-blocking)
            fetchWithAuth(`${BASE_URL}/api/ai/extract-media`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fileUrls: newFileUrls })
            })
            .then(res => res.json())
            .then(extractData => {
                if (extractData.success && extractData.urls && extractData.urls.length > 0) {
                    setFormData(prev => ({ ...prev, images: [...(prev.images || []), ...extractData.urls] }));
                }
            })
            .catch(err => console.error("Media extraction failed", err));
        } catch (error) {
            console.error("Reference upload failed", error);
            alert("일부 파일 업로드에 실패했습니다.");
        } finally {
            setIsAiUploading(false);
        }
    };

    const removeAiFileInline = (indexToRemove) => {
        setAiAttachedFiles(prev => prev.filter((_, i) => i !== indexToRemove));
        setAiFileUrls(prev => prev.filter((_, i) => i !== indexToRemove));
    };

    // -------------------------
    // Drag and Drop Handlers
    const [dragActiveAi, setDragActiveAi] = useState(false);
    const [dragActiveImg, setDragActiveImg] = useState(false);

    const handleDragAi = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActiveAi(true);
        else if (e.type === "dragleave") setDragActiveAi(false);
    };

    const handleDropAi = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActiveAi(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleAiFileUploadInline({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleDragImg = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActiveImg(true);
        else if (e.type === "dragleave") setDragActiveImg(false);
    };

    const handleDropImg = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActiveImg(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleGalleryUpload({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleAiGenerateInline = async () => {
        if (!formData.description.trim() && aiFileUrls.length === 0) {
            alert('스펙 문서 내용이나 참조 파일을 첨부해주세요. (설명 칸에 키워드를 넣어도 됩니다)');
            return;
        }

        setIsAiGenerating(true);
        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/ai/generate-draft`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ specs: formData.description, type: 'product', fileUrls: aiFileUrls, userPrompt: aiCustomPrompt })
            });

            if (!res.ok) throw new Error('API Request Failed');

            const reqData = await res.json();
            if (reqData.success && reqData.data) {
                setFormData(prev => ({
                    ...prev,
                    description: reqData.data.description || prev.description,
                    specs: reqData.data.specs && Array.isArray(reqData.data.specs) ? reqData.data.specs : prev.specs
                }));
            } else {
                alert('생성 실패: ' + (reqData.error || '알 수 없는 오류'));
            }
        } catch (error) {
            console.error('AI Generation error:', error);
            alert('AI 초안 생성 중 오류가 발생했습니다.');
        } finally {
            setIsAiGenerating(false);
        }
    };

    const handleAiImageInsert = (url) => {
        setFormData(prev => ({ ...prev, imageUrl: url }));
        alert('AI 이미지 생성이 완료되어 대표 이미지에 적용되었습니다!');
    };

    const [formData, setFormData] = useState({
        category: '', // Will default after fetching
        name: '',
        description: '',
        imageUrl: '',
        images: [],
        specs: []
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [productsData, categoriesData] = await Promise.all([
                getProducts(),
                getProductCategories()
            ]);
            setProducts(productsData);
            setCategories(categoriesData);
        } catch (error) {
            console.error("Failed to load data", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenProductModal = (product = null) => {
        if (product) {
            setIsEditMode(true);
            setEditingId(product.id);

            const specsArray = product.specs
                ? Object.entries(product.specs).map(([key, value]) => ({ key, value }))
                : [];

            setFormData({
                category: product.category,
                name: product.name,
                description: product.description || '',
                imageUrl: product.imageUrl || '',
                images: product.images || [],
                specs: specsArray
            });
        } else {
            setIsEditMode(false);
            setEditingId(null);
            setFormData({
                category: categories.length > 0 ? categories[0].id : '',
                name: '',
                description: '',
                imageUrl: '',
                images: [],
                specs: []
            });
        }
        setIsProductModalOpen(true);
    };

    const handleCloseProductModal = () => {
        setIsProductModalOpen(false);
        setFormData({ category: '', name: '', description: '', imageUrl: '', images: [], specs: [] });
        setAiFileUrls([]);
        setAiAttachedFiles([]);
        setIsAiGenerating(false);
        setIsAiUploading(false);
        setAiCustomPrompt('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Spec Management
    const addSpecRow = () => {
        setFormData(prev => ({
            ...prev,
            specs: [...prev.specs, { key: '', value: '' }]
        }));
    };

    const removeSpecRow = (index) => {
        setFormData(prev => ({
            ...prev,
            specs: prev.specs.filter((_, i) => i !== index)
        }));
    };

    const handleSpecChange = (index, field, value) => {
        const newSpecs = [...formData.specs];
        newSpecs[index][field] = value;
        setFormData(prev => ({ ...prev, specs: newSpecs }));
    };

    // Image Upload
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/upload?folder=tmp`, {
                method: 'POST',
                body: uploadData
            });

            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            const imageUrl = data.url || data.path;

            setFormData(prev => ({ ...prev, imageUrl: imageUrl }));
        } catch (error) {
            console.error("Upload failed", error);
            alert("이미지 업로드 실패");
        }
    };
    
    // Gallery Upload
    const handleGalleryUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const uploadedUrls = [];
        for (const file of files) {
            const uploadData = new FormData();
            uploadData.append('file', file);
            try {
                const res = await fetchWithAuth(`${BASE_URL}/api/upload?folder=tmp`, {
                    method: 'POST', body: uploadData
                });
                if (!res.ok) throw new Error('Upload failed');
                const data = await res.json();
                uploadedUrls.push(data.url || data.path);
            } catch (error) {
                console.error("Gallery upload failed", error);
            }
        }

        if (uploadedUrls.length > 0) {
            setFormData(prev => ({ ...prev, images: [...prev.images, ...uploadedUrls] }));
        }
        if (e.target && e.target.value !== undefined) {
            e.target.value = '';
        }
    };

    const removeGalleryImage = (index) => {
        setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    };

    const setAsMainImage = (index) => {
        setFormData(prev => {
            const selectedClone = prev.images[index];
            // Do not splice from images array! (Clone-Link Architecture)
            // This preserves the background context in the gallery while allowing independent AI stripping on the Main layer.
            return { ...prev, imageUrl: selectedClone };
        });
    };

    const handleSmartClipboardPaste = async () => {
        try {
            const clipboardItems = await navigator.clipboard.read();
            let textFound = false;
            let aiFiles = [];
            let galleryFiles = [];

            for (const item of clipboardItems) {
                if (item.types.includes('text/plain')) {
                    const blob = await item.getType('text/plain');
                    const text = await blob.text();
                    if (text.trim()) {
                        aiFiles.push(new File([text.trim()], `clipboard-text-${Date.now()}.txt`, { type: 'text/plain' }));
                        textFound = true;
                    }
                } else if (item.types.includes('text/html')) {
                    const blob = await item.getType('text/html');
                    const text = await blob.text();
                    if (text.trim()) {
                        aiFiles.push(new File([text.trim()], `clipboard-text-${Date.now()}.html`, { type: 'text/html' }));
                        textFound = true;
                    }
                }

                const imageType = item.types.find(type => type.startsWith('image/'));
                if (imageType) {
                    const blob = await item.getType(imageType);
                    galleryFiles.push(new File([blob], `clipboard-image-${Date.now()}.png`, { type: imageType }));
                }
            }

            if (aiFiles.length > 0) {
                await handleAiFileUploadInline({ target: { files: aiFiles } });
            }
            if (galleryFiles.length > 0) {
                await handleGalleryUpload({ target: { files: galleryFiles } });
            }

            if (!textFound && galleryFiles.length === 0) {
                alert('클립보드에 붙여넣을 텍스트나 이미지가 없습니다.');
            }
        } catch (err) {
            console.error('Smart clipboard paste failed:', err);
            alert('클립보드 접근 권한이 필요합니다. 브라우저 주소창 좌측 자물쇠 아이콘에서 클립보드를 [허용]해주세요.');
        }
    };

    const handleGalleryClipboardPaste = async () => {
        try {
            const clipboardItems = await navigator.clipboard.read();
            const imageFiles = [];
            for (const item of clipboardItems) {
                const imageType = item.types.find(type => type.startsWith('image/'));
                if (imageType) {
                    const blob = await item.getType(imageType);
                    imageFiles.push(new File([blob], `clipboard-gallery-${Date.now()}.png`, { type: imageType }));
                }
            }
            if (imageFiles.length > 0) {
                await handleGalleryUpload({ target: { files: imageFiles } });
            } else {
                alert('클립보드에 이미지가 없습니다.');
            }
        } catch (err) {
            console.error('Gallery clipboard paste failed:', err);
            alert('클립보드 접근 권한이 필요합니다. 브라우저 주소창 좌측 자물쇠 아이콘에서 클립보드를 [허용]해주세요.');
        }
    };

    const handleMainImageClipboardPaste = async () => {
        try {
            const clipboardItems = await navigator.clipboard.read();
            let imageFiles = [];
            for (const item of clipboardItems) {
                const imageType = item.types.find(type => type.startsWith('image/'));
                if (imageType) {
                    const blob = await item.getType(imageType);
                    imageFiles.push(new File([blob], `clipboard-main-${Date.now()}.png`, { type: imageType }));
                    break; // Main image only takes the first one
                }
            }
            if (imageFiles.length > 0) {
                await handleImageUpload({ target: { files: imageFiles } });
            } else {
                alert('클립보드에 이미지가 없습니다.');
            }
        } catch (err) {
            console.error('Main clipboard paste failed:', err);
            alert('클립보드 접근 권한이 필요합니다. 브라우저 주소창 좌측 자물쇠 아이콘에서 클립보드를 [허용]해주세요.');
        }
    };

    const handleBatchProcessImages = async () => {
        if (!formData.images || formData.images.length === 0) return alert('하단 갤러리에 변환할 이미지가 없습니다.');
        setIsProcessingImage(true);
        let processedUrls = [...formData.images];
        let successCount = 0;
        
        for (let i = 0; i < processedUrls.length; i++) {
            try {
                const res = await fetchWithAuth(`${BASE_URL}/api/ai/process-image`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ imageUrl: processedUrls[i] })
                });
                
                if (res.ok) {
                    const data = await res.json();
                    if (data.success && data.url) {
                        processedUrls[i] = data.url;
                        successCount++;
                    }
                }
            } catch (e) {
                console.error("Processing index " + i + " failed", e);
            }
        }
        
        setFormData(prev => ({ ...prev, images: processedUrls }));
        setIsProcessingImage(false);
        alert(`총 ${successCount}장의 이미지 배경 제거 및 16:9 쾌속 정렬이 완료되었습니다.`);
    };

    const handleBatchCropImages = async () => {
        if (!formData.images || formData.images.length === 0) return alert('하단 갤러리에 변환할 이미지가 없습니다.');
        if (!window.confirm('갤러리의 모든 원본 이미지를 16:9 비율 최적화(Crop) 규격으로 일괄 자릅니다. (배경 유지) 계속하시겠습니까?')) return;
        
        setIsCropping(true);
        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/ai/batch-crop-images`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imageUrls: formData.images })
            });
            if (!res.ok) throw new Error('Batch crop failed');
            const data = await res.json();
            if (data.success && data.urls) {
                setFormData(prev => ({ ...prev, images: data.urls }));
                alert('모든 이미지가 배경 훼손 없이 16:9 규격으로 찌그러짐 없이 시원하게 잘라/크롭 되었습니다!');
            }
        } catch (error) {
            console.error(error);
            alert('이미지 일괄 크롭 처리 중 오류가 발생했습니다.');
        } finally {
            setIsCropping(false);
        }
    };

    const handleProcessImage = async () => {
        if (!formData.imageUrl) return alert('먼저 이미지를 업로드해야 배경 제거를 할 수 있습니다.');
        
        setIsProcessingImage(true);
        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/ai/process-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imageUrl: formData.imageUrl })
            });
            
            if (!res.ok) throw new Error('API Request Failed');
            
            const data = await res.json();
            if (data.success && data.url) {
                setFormData(prev => ({ ...prev, imageUrl: data.url }));
            } else {
                alert('처리 실패: ' + (data.error || '알 수 없는 오류'));
            }
        } catch (error) {
            console.error('Image processing error:', error);
            alert('이미지 처리 중 오류가 발생했습니다.');
        } finally {
            setIsProcessingImage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            // Convert specs array to object
            const specsObject = formData.specs.reduce((acc, curr) => {
                if (curr.key.trim()) {
                    acc[curr.key.trim()] = curr.value.trim();
                }
                return acc;
            }, {});

            const payload = {
                category: formData.category,
                name: formData.name,
                description: formData.description,
                imageUrl: formData.imageUrl,
                images: formData.images,
                specs: specsObject
            };

            if (isEditMode) {
                await updateProduct(editingId, payload);
                alert('수정되었습니다.');
            } else {
                await createProduct(payload);
                alert('등록되었습니다.');
            }

            setIsProductModalOpen(false);
            loadData(); // Reload all data
        } catch (error) {
            console.error("Failed to save product", error);
            alert("저장에 실패했습니다.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            try {
                await deleteProduct(id);
                alert('삭제되었습니다.');
                loadData();
            } catch (error) {
                console.error("Failed to delete product", error);
                alert('삭제 실패');
            }
        }
    };

    // Reorder Modal Functions
    const openReorderModal = () => {
        setReorderList([...products]);
        setIsReorderModalOpen(true);
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newList = [...reorderList];
        const [draggedItem] = newList.splice(draggedIndex, 1);
        newList.splice(index, 0, draggedItem);
        setReorderList(newList);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleSaveReorder = async () => {
        try {
            const ids = reorderList.map(item => item.id);
            await reorderProducts(ids);
            alert('순서가 저장되었습니다.');
            setIsReorderModalOpen(false);
            loadData();
        } catch (error) {
            console.error(error);
            alert('순서 저장 실패');
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">제품 관리</h2>
                <div className="flex flex-wrap gap-2 w-full md:w-auto">
                    <AdminButton variant="secondary" onClick={() => setIsCategoryModalOpen(true)}>
                        ⚙️ 카테고리 관리
                    </AdminButton>
                    <AdminButton variant="primary" onClick={() => handleOpenProductModal(null)}>
                        + 제품 등록
                    </AdminButton>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex justify-end p-4 border-b border-gray-100 bg-gray-50/50">
                    <AdminButton
                        onClick={openReorderModal}
                        variant="secondary" size="sm"
                    >
                        ↕ 순서 변경
                    </AdminButton>
                </div>
                {loading ? (
                    <div className="p-10 text-center text-gray-400 font-bold animate-pulse">데이터 로딩 중...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="px-5 py-4 font-bold w-20">이미지</th>
                                    <th className="px-5 py-4 font-bold w-32">카테고리</th>
                                    <th className="px-5 py-4 font-bold">제품명</th>
                                    <th className="px-5 py-4 font-bold">설명</th>
                                    <th className="px-5 py-4 font-bold w-32 text-center">관리</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-gray-700">
                                {products.map(product => (
                                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-5 py-3">
                                            {product.imageUrl ? (
                                                <img src={product.imageUrl} alt={product.name} className="w-12 h-12 object-cover rounded-md shadow-sm border border-gray-100" />
                                            ) : (
                                                <div className="w-12 h-12 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-xs text-gray-400">N/A</div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 font-semibold"><span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-md text-xs">{categories.find(c => c.id === product.category || c.code === product.category || c.id === Number(product.category))?.label || product.category || '-'}</span></td>
                                        <td className="px-5 py-3 font-extrabold text-gray-900 text-base">{product.name}</td>
                                        <td className="px-5 py-3 text-gray-500 max-w-xs truncate">
                                            {product.description}
                                        </td>
                                        <td className="px-5 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <AdminButton variant="secondary" size="sm" onClick={() => handleOpenProductModal(product)}>수정</AdminButton>
                                                <AdminButton variant="danger" size="sm" onClick={() => handleDelete(product.id)}>삭제</AdminButton>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {products.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="p-10 text-center text-gray-400 font-bold bg-gray-50">등록된 제품이 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Product Modal */}
            {isProductModalOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 outline-none" 
                    tabIndex="-1"
                >
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <h3 className="text-xl font-bold text-gray-800 m-0">{isEditMode ? '제품 수정' : '새 제품 등록'}</h3>
                            </div>
                            <button onClick={handleCloseProductModal} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none bg-transparent outline-none">&times;</button>
                        </div>
                        <div className="p-6 overflow-y-auto w-[640px] max-w-full">
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700">카테고리</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm"
                                        required
                                    >
                                        <option value="" disabled>분류를 선택하세요</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700">제품명</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm font-bold text-gray-900"
                                        placeholder="예: Atec ESL 2.9"
                                    />
                                </div>

                                <div className="space-y-3">
                                    {/* Inline AI Assistant */}
                                    <div 
                                        className={`p-4 rounded-xl border transition-all duration-200 ${dragActiveAi ? 'bg-pink-100 border-pink-400 border-dashed scale-[1.01]' : 'bg-gradient-to-r from-pink-50 to-white border-pink-100 shadow-sm'}`}
                                        onDragEnter={handleDragAi} onDragOver={handleDragAi} onDragLeave={handleDragAi} onDrop={handleDropAi}
                                    >
                                        <div className="flex flex-col gap-3 mb-3 pointer-events-none">
                                            <label className="block text-sm font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-pink-500 m-0">✨ AI 제품 스펙 자동 생성 (PPT 파일 동시 지원)</label>
                                            
                                            <div className="flex flex-col sm:flex-row gap-2 pointer-events-auto">
                                                <input 
                                                    type="url" 
                                                    value={aiScrapeUrl} 
                                                    onChange={(e) => setAiScrapeUrl(e.target.value)} 
                                                    placeholder="타사 몰 제품 URL 복사 붙여넣기" 
                                                    disabled={isScraping || isAiGenerating || isAiUploading}
                                                    className="flex-1 px-3 py-1.5 border border-pink-200 rounded shadow-sm text-xs focus:ring-1 focus:ring-pink-400 outline-none"
                                                />
                                                <AdminButton type="button" variant="primary" size="sm" onClick={handleScrapeUrl} disabled={!aiScrapeUrl || isScraping || isAiGenerating || isAiUploading} className="!bg-pink-600 hover:bg-pink-700 border-0 shadow-sm shrink-0 text-xs justify-center whitespace-nowrap">
                                                    {isScraping ? '⏳ 추출 중...' : '🔗 불러오기'}
                                                </AdminButton>
                                            </div>

                                            <div className="flex flex-wrap gap-2 pointer-events-auto">
                                                <AdminButton type="button" variant="primary" size="sm" onClick={handleAiGenerateInline} disabled={isAiGenerating || isAiUploading || isScraping} className="!bg-gradient-to-r from-primary-600 to-pink-500 hover:opacity-90 border-0 shadow-sm shrink-0 text-xs flex-1 sm:flex-none justify-center">
                                                    {isAiGenerating ? '⏳ 작성 중...' : '✨ 자동 생성'}
                                                </AdminButton>
                                                <button type="button" onClick={handleSmartClipboardPaste} disabled={isAiGenerating || isAiUploading || isScraping} className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded shadow-sm hover:bg-gray-50 text-xs font-bold shrink-0 flex items-center justify-center gap-1 transition-colors">
                                                    📋 붙여넣기
                                                </button>
                                            </div>
                                        </div>
                                        <div className="relative mb-3">
                                            <div className={`absolute inset-0 flex items-center justify-center border-2 border-dashed rounded-lg pointer-events-none transition-colors ${dragActiveAi ? 'border-primary-500 bg-primary-50/50' : 'border-pink-200 bg-white/50'}`}>
                                                <span className="text-sm font-bold text-pink-400">여기로 문서들을 드래그 앤 드롭 하세요 (PDF, PPTX)</span>
                                            </div>
                                            <input 
                                                type="file" multiple accept=".pdf,.ppt,.pptx,image/png,image/jpeg,image/webp" 
                                                onChange={handleAiFileUploadInline} 
                                                disabled={isAiGenerating || isAiUploading}
                                                className="block w-full text-transparent file:mr-2 file:py-3 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-pink-500 file:text-white hover:file:bg-pink-600 cursor-pointer shadow-sm relative z-10 opacity-0 h-14"
                                            />
                                            {isAiUploading && <span className="text-xs font-bold text-blue-500 animate-pulse mt-1 block text-center">업로드 및 이미지 추출 진행 중...</span>}
                                        </div>
                                        {aiAttachedFiles.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-3">
                                                {aiAttachedFiles.map((file, idx) => (
                                                    <div key={idx} className="flex items-center gap-2 bg-pink-50 text-pink-700 text-xs font-bold px-2 py-1.5 rounded-lg border border-pink-200 max-w-full">
                                                        <span className="truncate flex-1 max-w-[200px]" title={file.name}>📎 {file.name}</span>
                                                        <button type="button" onClick={() => removeAiFileInline(idx)} className="text-pink-600 hover:bg-pink-100 rounded-full w-5 h-5 flex items-center justify-center transition-colors">✕</button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            rows="3"
                                            disabled={isAiGenerating}
                                            className="w-full px-4 py-3 bg-white border border-pink-200 rounded-xl focus:ring-2 focus:ring-pink-400 outline-none shadow-sm text-sm text-gray-700 leading-relaxed font-medium disabled:opacity-50"
                                            placeholder="참조 문서를 올리시거나 이곳에 특징 키워드를 적고 '자동 생성'을 누르시면, AI 텍스트와 하단 스펙 항목이 병렬로 자동 작성됩니다."
                                        />
                                        <AiPresetManager type="product" value={aiCustomPrompt} onChange={setAiCustomPrompt} disabled={isAiGenerating || isAiUploading} />
                                    </div>
                                </div>

                                {/* Main Profile Image */}
                                <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                                    <label className="block text-sm font-bold text-gray-700">대표 이미지 (투명배경 PNG 권장)</label>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input type="file" onChange={handleImageUpload} className="block w-full text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:font-bold file:bg-white file:border-gray-200 file:text-gray-700 hover:file:bg-gray-100 cursor-pointer shadow-sm basis-full mb-2" accept="image/*" />
                                        <AdminButton type="button" variant="primary" size="sm" onClick={() => setIsAiImageModalOpen(true)} className="!bg-gradient-to-r from-blue-600 to-indigo-500 border-0 shadow-sm shrink-0 text-xs flex-1 sm:flex-none justify-center">
                                            🎨 AI 기어 생성
                                        </AdminButton>
                                        <AdminButton type="button" variant="primary" size="sm" disabled={!formData.imageUrl || isProcessingImage} onClick={handleProcessImage} className="!bg-gradient-to-r from-emerald-500 to-teal-500 border-0 shadow-sm shrink-0 text-xs flex-1 sm:flex-none justify-center">
                                            {isProcessingImage ? '⏳ 자동 처리중...' : '✨ 투명 배경(배열 제거 & 16:9 정렬)'}
                                        </AdminButton>
                                        <button type="button" onClick={handleMainImageClipboardPaste} disabled={isProcessingImage} className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded shadow-sm hover:bg-gray-50 text-xs font-bold shrink-0 flex items-center justify-center gap-1 transition-colors">
                                            📋 붙여넣기
                                        </button>
                                    </div>
                                    {formData.imageUrl && (
                                        <div className="mt-3 bg-white p-2 rounded-lg border border-gray-200 inline-block shadow-sm">
                                            <img src={formData.imageUrl} alt="Preview" className="h-24 object-contain rounded" />
                                        </div>
                                    )}
                                </div>

                                {/* Gallery Upload */}
                                <div 
                                    className={`space-y-4 p-5 rounded-xl border border-dashed transition-all duration-200 ${dragActiveImg ? 'bg-emerald-50 border-emerald-400 scale-[1.01]' : 'bg-gray-50 border-gray-300'}`}
                                    onDragEnter={handleDragImg} onDragOver={handleDragImg} onDragLeave={handleDragImg} onDrop={handleDropImg}
                                >
                                    <div className="flex flex-col gap-3 pointer-events-none">
                                        <label className="block text-base font-bold text-gray-800 m-0">📸 보조 갤러리 이미지 (다중 가능)</label>
                                        <div className="flex flex-wrap gap-2 pointer-events-auto">
                                            <AdminButton type="button" onClick={handleBatchCropImages} disabled={isCropping || isProcessingImage || formData.images.length === 0} className="!bg-gray-800 text-white border-0 shadow-sm shrink-0 text-xs hover:bg-gray-900 flex-1 sm:flex-none justify-center">
                                                {isCropping ? '⏳ 크롭 중...' : '📐 원본 16:9 크롭 (배경 유지)'}
                                            </AdminButton>
                                            <AdminButton type="button" onClick={handleBatchProcessImages} disabled={isProcessingImage || isCropping || formData.images.length === 0} className="!bg-gradient-to-r from-emerald-500 to-teal-500 border-0 shadow-sm shrink-0 text-xs flex-1 sm:flex-none justify-center">
                                                {isProcessingImage ? '⏳ 배경 제거 처리중...' : '✨ 투명 배경 16:9 피팅 (배경 날림)'}
                                            </AdminButton>
                                            <button type="button" onClick={handleGalleryClipboardPaste} disabled={isProcessingImage || isCropping} className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded shadow-sm hover:bg-gray-50 text-xs font-bold shrink-0 flex items-center justify-center gap-1 transition-colors">
                                                📋 붙여넣기
                                            </button>
                                        </div>
                                    </div>

                                    <div className="relative mt-2">
                                        <div className={`absolute inset-0 flex items-center justify-center border-2 border-dashed rounded-lg pointer-events-none transition-colors ${dragActiveImg ? 'border-emerald-400 bg-emerald-100/30' : 'border-gray-200 bg-white/70'}`}>
                                            <span className="text-sm font-bold text-emerald-600/70">여기로 갤러리 이미지를 드롭하세요 (JPG/PNG)</span>
                                        </div>
                                        <input type="file" multiple onChange={handleGalleryUpload} accept="image/*" className="block w-full text-transparent file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-emerald-600 file:text-white hover:file:bg-emerald-700 cursor-pointer relative z-10 opacity-0 h-14" />
                                    </div>
                                    
                                    {formData.images && formData.images.length > 0 && (
                                        <div className="flex flex-wrap gap-4 mt-4 bg-white p-4 rounded-xl border border-gray-200">
                                            {formData.images.map((img, idx) => (
                                                <div key={idx} className="relative group border border-gray-200 rounded-lg p-1 w-24 h-24 flex items-center justify-center bg-gray-50 overflow-hidden shadow-sm">
                                                    <img src={img} alt={`Gallery ${idx}`} className="max-w-full max-h-full object-contain" />
                                                    <button type="button" onClick={() => removeGalleryImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity font-bold shadow-md z-10" title="삭제">✕</button>
                                                    <button type="button" onClick={() => setAsMainImage(idx)} className="absolute bottom-1 left-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-md px-2 py-0.5 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity font-extrabold shadow-md z-10 hover:scale-105" title="대표 이미지로 승격">⭐ 메인 지정</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Dynamic Specs Editor */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center bg-gray-800 text-white p-3 rounded-t-xl">
                                        <span className="text-sm font-bold">상세 규격 (Key : Value)</span>
                                        <AdminButton type="button" variant="secondary" size="sm" onClick={addSpecRow}>
                                            + 항목 추가
                                        </AdminButton>
                                    </div>
                                    <div className="bg-gray-50 p-4 border border-x-gray-200 border-b-gray-200 rounded-b-xl max-h-60 overflow-y-auto space-y-2">
                                        {formData.specs.length === 0 && <div className="text-gray-400 text-sm font-bold text-center py-4">규격 항목을 추가해주세요.</div>}
                                        {formData.specs.map((spec, index) => (
                                            <div key={index} className="flex gap-2 items-center">
                                                <input
                                                    type="text"
                                                    placeholder="항목 (예: 디스플레이)"
                                                    value={spec.key}
                                                    onChange={(e) => handleSpecChange(index, 'key', e.target.value)}
                                                    className="w-1/3 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium focus:ring-1 focus:ring-primary-500 outline-none"
                                                />
                                                <span className="text-gray-400 font-bold">:</span>
                                                <input
                                                    type="text"
                                                    placeholder="내용 (예: 1.54인치 E-paper)"
                                                    value={spec.value}
                                                    onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                                                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary-500 outline-none"
                                                />
                                                <button type="button" onClick={() => removeSpecRow(index)} className="w-8 h-8 flex items-center justify-center shrink-0 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors shadow-sm font-bold">✕</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-4 border-t border-gray-100">
                                    <AdminButton type="button" variant="secondary" onClick={handleCloseProductModal} fullWidth>취소</AdminButton>
                                    <AdminButton type="submit" variant="primary" disabled={isSaving} fullWidth>
                                        {isSaving ? '저장 중...' : (isEditMode ? '수정 사항 저장' : '새 제품 등록')}
                                    </AdminButton>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}


            {/* Ai Image Generator Modal */}
            <AiImageGeneratorModal 
                isOpen={isAiImageModalOpen} 
                onClose={() => setIsAiImageModalOpen(false)} 
                onInsert={handleAiImageInsert}
            />

            {/* Category Manager Modal */}
            <ProductCategoryManager
                isOpen={isCategoryModalOpen}
                onClose={() => {
                    setIsCategoryModalOpen(false);
                    loadData(); // Refresh data on close in case categories changed
                }}
            />

            {/* Reorder Modal */}
            {isReorderModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden p-6">
                        <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100">
                            <h3 className="text-xl font-bold text-gray-800 m-0">제품 순서 변경</h3>
                            <button onClick={() => setIsReorderModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none bg-transparent outline-none">&times;</button>
                        </div>
                        <p className="text-sm text-gray-500 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100 font-medium">
                            드래그하여 순서를 변경하세요. 적용 시 상단부터 재정렬됩니다.
                        </p>
                        <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                            {reorderList.map((item, index) => (
                                <div
                                    key={item.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragEnd={handleDragEnd}
                                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-grab transition-colors active:cursor-grabbing ${draggedIndex === index ? 'bg-primary-50 border-primary-200 shadow-md scale-[1.02]' : 'bg-white border-gray-200 shadow-sm hover:border-gray-300'}`}
                                >
                                    <span className="text-gray-400 text-lg cursor-grab w-5 text-center">☰</span>
                                    <span className="w-6 h-6 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs font-bold shadow-sm shrink-0">
                                        {index + 1}
                                    </span>
                                    {item.imageUrl ? (
                                        <img src={item.imageUrl} alt="" className="w-10 h-10 object-cover rounded-md shadow-sm border border-gray-100" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-md bg-gray-100 border border-gray-200"></div>
                                    )}
                                    <div className="flex-1 overflow-hidden">
                                        <div className="font-bold text-gray-800 text-sm truncate">{item.name}</div>
                                        <div className="text-xs text-gray-500 font-medium mt-0.5 truncate">
                                            {categories.find(c => c.id === item.category || c.code === item.category)?.label || '-'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100 shrink-0">
                            <AdminButton
                                onClick={() => setIsReorderModalOpen(false)}
                                variant="secondary" fullWidth
                            >
                                취소
                            </AdminButton>
                            <AdminButton
                                onClick={handleSaveReorder}
                                variant="primary" fullWidth
                            >
                                순서 변경 적용
                            </AdminButton>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default ProductManagement;
